import Select, {
  ActionMeta,
  CSSObjectWithLabel,
  GroupBase,
  MultiValue,
  OptionsOrGroups,
  PropsValue,
  SingleValue,
} from 'react-select';

type IOptions<T> = OptionsOrGroups<T, GroupBase<T>>;

export type IOnChange<T> = (
  newValue: MultiValue<T> | SingleValue<T>,
  actionMeta: ActionMeta<T>
) => void;

interface ISelectInput<T> {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  options: IOptions<T>;
  required?: boolean;
  singleSelect?: boolean;
  value?: PropsValue<T>;
  handleChange: IOnChange<T>;
  filterOption?: (option: any, searchText: string) => boolean;
  getOptionLabel?: (x: T) => string;
  getOptionValue?: (x: T) => string;
}

export const SelectInput = <T,>({
  name,
  label,
  placeholder,
  options,
  required,
  singleSelect,
  className,
  value,
  handleChange,
  filterOption,
  getOptionLabel,
  getOptionValue,
}: ISelectInput<T>) => {
  return (
    <div className={`flex flex-col w-full ${className}`}>
      {label && (
        <label className="medium text-sm text-neutral-900 flex justify-between items-center mb-2">
          <span>{label}</span>
          {required && <span className="text-primary-700">*</span>}
        </label>
      )}
      <Select
        isMulti={singleSelect ? false : true}
        name={name}
        placeholder={placeholder}
        styles={selectInputStyles}
        onChange={handleChange}
        value={value}
        options={options}
        filterOption={filterOption}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
      />
    </div>
  );
};

// Matches the app's native inputs and the MultiSelect control so selects line
// up consistently across forms (same bg, border, radius and 44px height).
const selectInputStyles = {
  control: (base: CSSObjectWithLabel, state: { isFocused: boolean }) => ({
    ...base,
    backgroundColor: '#FCFCFC',
    borderColor: state.isFocused ? '#C72C3F' : '#DCE2EA',
    borderRadius: '0.5rem',
    padding: '4px 4px',
    minHeight: '44px',
    boxShadow: state.isFocused ? '0 0 0 1px #C72C3F14' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#C72C3F' : '#DCE2EA' },
  }),
  placeholder: (base: CSSObjectWithLabel) => ({ ...base, color: '#A5A7AC', fontSize: '0.875rem' }),
  input: (base: CSSObjectWithLabel) => ({ ...base, fontSize: '0.875rem', color: '#111111' }),
  singleValue: (base: CSSObjectWithLabel) => ({ ...base, fontSize: '0.875rem', color: '#111111' }),
  multiValue: (base: CSSObjectWithLabel) => ({ ...base, backgroundColor: '#F5F5F6', borderRadius: '0.5rem', padding: '2px 4px' }),
  multiValueLabel: (base: CSSObjectWithLabel) => ({ ...base, fontSize: '0.875rem', color: '#111111' }),
  multiValueRemove: (base: CSSObjectWithLabel) => ({ ...base, color: '#A5A7AC', '&:hover': { backgroundColor: 'transparent', color: '#111111' } }),
  indicatorSeparator: (base: CSSObjectWithLabel) => ({ ...base, backgroundColor: '#DCE2EA' }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({ ...base, color: '#A5A7AC' }),
  menu: (base: CSSObjectWithLabel) => ({ ...base, borderRadius: '0.5rem', border: '1px solid #DCE2EA', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }),
  option: (base: CSSObjectWithLabel, state: { isFocused: boolean }) => ({ ...base, fontSize: '0.875rem', backgroundColor: state.isFocused ? '#F8F9FA' : 'white', color: '#111111', '&:active': { backgroundColor: '#F5F5F6' } }),
};
